import * as Yup from "yup";
import { stringWithNoSpecialChars, validateDocumentCategory, validateDocumentType, validateFileByExtention } from "@/Shared/Helpers";
import { DocumentEnums, DOMAIN_TYPES, UPLOAD_ALLOWED_DOC_FILE_EXTENTIONS, UPLOAD_MAX_DOC_FILE_SIZE } from "@/Shared/Types";

/**
 * =====================================================
 * Document Form Types
 * =====================================================
 *
 * Core form fields, optional upload fields, and
 * source/resource values for documents.
 */
const docFileOptions = { 
	maxSizeInMB: UPLOAD_MAX_DOC_FILE_SIZE, 
	allowedExtentions: UPLOAD_ALLOWED_DOC_FILE_EXTENTIONS 
};

export interface DocumentCoreValues {
	id?: number;
	documentTitle: string;
	documentCategory: string;
	documentType: string;
}

export type DocumentUploadValues = {
	uploadedFile?: File;
	documentSource?: string;
};

export type DocumentSourceValues = {
	resourceId: number;
};

export type DocumentFormValues = DocumentCoreValues & Partial<DocumentUploadValues> & Partial<DocumentSourceValues>;


/**
 * =====================================================
 * DocumentValidationSchema
 * =====================================================
 *
 * Generates a Yup validation schema for document forms.
 *
 * Responsibilities:
 * - Ensures required fields for creation/update
 * - Validates file uploads by extension and size
 * - Validates document title and enums for category/type
 * - Handles conditional rules for resource ID based on category
 *
 * @param viewEnums DocumentEnums used for category/type validation
 * @returns Yup.ObjectSchema<DocumentFormValues>
 */
export const DocumentValidationSchema = (viewEnums: DocumentEnums) =>
	Yup.object({
		id: Yup.number().nullable().optional(),

		/**
         * resourceId is required only if documentCategory
         * matches one of the domain types (Mandate, Lease, Property, etc.)
         */
		resourceId: Yup.number()
			.when('documentCategory', {
				is: (category: string) =>  Object.values(DOMAIN_TYPES).includes(category as DOMAIN_TYPES),
				then: schema =>
					schema.required("l'id de la resource est requise pour cette catégorie.")
				.moreThan(0, "Veuillez séléectionner la resource est requise pour cette catégorie."),
				    
				otherwise: schema =>
					schema.optional().nullable(),
			}),

		documentTitle: stringWithNoSpecialChars({ minLength: 3, maxLength: 255 })
			.required("Le titre du document est requis."),

		//uploadedFile: validateFileByExtention(docFileOptions).required('Le fichier est requise.'),
		documentType: validateDocumentType(viewEnums.DocumentTypeEnum),
		documentCategory: validateDocumentCategory(viewEnums.DocumentCategoryEnum),

		documentSource: Yup.string()
			.url("L'URL source est invalide.")
			.nullable()
			.optional(),

		
		/**
         * uploadedFile rules:
         * - CREATION mode (id absent): file required
         * - UPDATE mode (id present): file must NOT be provided
         */
		uploadedFile: Yup.mixed().when("resourceId", (resourceId, schema) => {
			if (!resourceId) {
				// MODE CREATION : id absent → fichier obligatoire
				return validateFileByExtention(docFileOptions)
					.required("Le fichier est requis.");
			}

			// MODE UPDATE : id présent → fichier interdit
			return Yup.mixed().test(
				"file-not-allowed",
				"Le fichier ne doit pas être fourni lors de la mise à jour.",
				(value) => value === undefined || value === null
			);
		}),

	});
